/**
 * ReferralSection
 *
 * Dashboard widget that shows the user:
 *   - Their unique referral link (copy + share)
 *   - How many friends have signed up / converted to paid
 *   - Rewards earned ($7.99 credit per paid conversion)
 *
 * Reward logic: when a friend they referred makes their first payment,
 * the backend applies a $7.99 Stripe balance credit automatically.
 */

import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../../lib/api';

interface ReferralStats {
  referral_code:   string;
  share_url:       string;
  referral_count:  number;
  total_signups:   number;
  total_converted: number;
  total_rewarded:  number;
  rewards: {
    id:          string;
    reward_type: string;
    applied:     boolean;
    applied_at:  string | null;
    created_at:  string;
  }[];
}

const REWARD_LABEL: Record<string, string> = {
  free_month:              '1 month free ($7.99 credit)',
  free_card:               '1 free card send',
  tier_upgrade_3mo:        '3-month tier upgrade',
  tier_upgrade_permanent:  'Permanent tier upgrade',
};

export default function ReferralSection() {
  const [stats,   setStats]   = useState<ReferralStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied,  setCopied]  = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const load = useCallback(async () => {
    try {
      const json = await apiFetch<ReferralStats>('/api/referrals/me');
      setStats(json);
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function copyLink() {
    if (!stats) return;
    await navigator.clipboard.writeText(stats.share_url).catch(() => {
      prompt('Copy your referral link:', stats.share_url);
    });
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  }

  function shareNative() {
    if (!stats) return;
    if (navigator.share) {
      navigator.share({
        title: 'MomentSent, automated cards for people you love',
        text:  'I use MomentSent to automatically send physical cards to my friends & family. Try it free for 14 days!',
        url:   stats.share_url,
      }).catch(() => {});
    } else {
      copyLink();
    }
  }

  if (loading) {
    return (
      <section className="mb-10">
        <div className="h-5 w-36 bg-gray-100 rounded-lg mb-4 animate-pulse" />
        <div className="h-28 rounded-[20px] bg-white shadow-warm animate-pulse" />
      </section>
    );
  }

  if (!stats) return null;

  const hasRewards    = stats.rewards.length > 0;
  const creditEarned  = stats.total_rewarded * 7.99;

  return (
    <section className="mb-10">
      <h2 className="font-heading text-lg font-semibold text-navy mb-4">Refer friends</h2>

      <div className="bg-white rounded-[20px] shadow-warm border border-gray-100 overflow-hidden">

        {/* Top bar — reward tally */}
        {hasRewards && (
          <div className="bg-gold/10 border-b border-gold/20 px-5 py-3 flex items-center gap-2">
            <span className="text-lg">🎁</span>
            <p className="text-sm font-medium text-gold">
              You've earned <strong>${creditEarned.toFixed(2)}</strong> in referral credits
              {' '}({stats.total_rewarded} paid conversion{stats.total_rewarded !== 1 ? 's' : ''})
            </p>
          </div>
        )}

        <div className="p-5 space-y-4">

          {/* Headline + stats row */}
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="font-semibold text-navy text-sm">
                Earn $7.99 for every friend who subscribes
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                Share your link · Friend signs up · You get a free month automatically
              </p>
            </div>
            <div className="flex gap-4 flex-shrink-0 text-center">
              <div>
                <p className="text-xl font-bold text-navy">{stats.total_signups}</p>
                <p className="text-xs text-gray-400">signed up</p>
              </div>
              <div>
                <p className="text-xl font-bold text-sage">{stats.total_converted}</p>
                <p className="text-xs text-gray-400">subscribed</p>
              </div>
            </div>
          </div>

          {/* Referral link */}
          <div className="flex gap-2">
            <div className="flex-1 flex items-center rounded-xl border border-gray-200 bg-gray-50 px-3 overflow-hidden">
              <span className="text-xs font-mono text-gray-500 truncate">
                {stats.share_url}
              </span>
            </div>
            <button
              onClick={copyLink}
              className={[
                'flex-shrink-0 flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-all',
                copied
                  ? 'bg-sage text-navy'
                  : 'bg-navy text-white hover:bg-navy/85',
              ].join(' ')}
            >
              {copied ? (
                <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg> Copied!</>
              ) : (
                <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg> Copy</>
              )}
            </button>
            {/* Native share (mobile) */}
            <button
              onClick={shareNative}
              className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-sm
                         font-medium border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
              title="Share"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
            </button>
          </div>

          {/* Your code — big and copiable */}
          <div className="flex items-center justify-between py-2.5 px-4 rounded-xl bg-navy/5 border border-navy/10">
            <div>
              <p className="text-xs text-gray-500 mb-0.5">Your referral code</p>
              <p className="font-mono font-bold text-navy text-lg tracking-widest">
                {stats.referral_code}
              </p>
            </div>
            <p className="text-xs text-gray-400 text-right max-w-[140px]">
              Friends enter this at signup if they don't have your link
            </p>
          </div>

          {/* Rewards detail — collapsible */}
          {hasRewards && (
            <div>
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="flex items-center gap-1 text-xs font-medium text-sage hover:text-sage/70 transition-colors"
              >
                <svg
                  className={`w-3.5 h-3.5 transition-transform ${showDetails ? 'rotate-90' : ''}`}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                {showDetails ? 'Hide' : 'View'} reward history
              </button>

              {showDetails && (
                <div className="mt-3 space-y-2">
                  {stats.rewards.map((r) => (
                    <div
                      key={r.id}
                      className="flex items-center justify-between px-3 py-2 rounded-lg bg-gray-50 border border-gray-100"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-base">🎁</span>
                        <p className="text-xs font-medium text-navy">
                          {REWARD_LABEL[r.reward_type] ?? r.reward_type}
                        </p>
                      </div>
                      <span className={[
                        'text-xs font-semibold px-2 py-0.5 rounded-full',
                        r.applied ? 'bg-sage/10 text-sage' : 'bg-gold/10 text-gold',
                      ].join(' ')}>
                        {r.applied ? 'Applied ✓' : 'Pending'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
